import React, { useRef, useState, useEffect, useCallback, useContext } from 'react';
import { Toast } from 'primereact/toast';
import axios from 'axios';
import { UserContext } from './hooks/UserContext';

const ImageUploader = ({ uploadFolder }) => {
  const { user } = useContext(UserContext);
  const [folder, setFolder] = useState();
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const toast = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    setFolder(uploadFolder);
  }, [uploadFolder]);

  const uploadFile = useCallback(async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', folder);

    try {
      setUploading(true);
      const res = await axios.post('/api/upload', formData, {
        headers: { 'Authorization': `Bearer ${user.token}` },
      });
      const url = res.data.location;
      setUploadedFiles(prev => [...prev, { name: file.name, url }]);
      toast.current.show({ severity: 'info', summary: 'Uploaded', detail: file.name, life: 3000 });
    } catch (error) {
      console.error(error);
      toast.current.show({ severity: 'error', summary: 'Upload failed', detail: file.name, life: 3000 });
    } finally {
      setUploading(false);
    }
  }, [folder, user]);

  const handleFiles = useCallback((files) => {
    for (const file of files) {
      if (file.type.startsWith('image/')) {
        uploadFile(file);
      }
    }
  }, [uploadFile]);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleInputChange = (e) => {
    handleFiles(e.target.files);
    e.target.value = '';
  };

  const copyUrl = (url) => {
    navigator.clipboard.writeText(url);
    toast.current.show({ severity: 'info', summary: 'Copied', detail: 'URL copied to clipboard', life: 2000 });
  };

  return (
    <div className="ne-image-uploader">
      <Toast ref={toast} />
      <div
        className={`ne-upload-dropzone ${isDragging ? 'ne-upload-dragging' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleInputChange}
          style={{ display: 'none' }}
        />
        <i className="pi pi-image" />
        <span>
          {uploading ? 'Uploading...' : 'Drop image here or click to upload'}
        </span>
      </div>

      {uploadedFiles.length > 0 && (
        <div className="ne-uploaded-list">
          {uploadedFiles.map((file, i) => (
            <div key={i} className="ne-uploaded-item" onClick={() => copyUrl(file.url)} title="Click to copy URL">
              <i className="pi pi-check-circle" />
              <span className="ne-uploaded-name">{file.name}</span>
              <i className="pi pi-copy ne-uploaded-copy" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ImageUploader;
